import { useRef, useState, useCallback, memo, useMemo, useEffect } from 'react';
import { InstantSearch, Configure, useSearchBox, useHits } from 'react-instantsearch';
import { Hit } from 'instantsearch.js';
import { algoliasearch } from 'algoliasearch';
import { Input, Spin, Empty } from 'antd';
import {
	SearchOutlined,
	PlayCircleOutlined,
	FilePdfOutlined,
	ReadOutlined,
	BookOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import './AcademySearch.scss';

export interface AcademyHit {
	objectID: string;
	type: 'video' | 'pdf' | 'tutorial';
	chapterId: number;
	chapterTitle: string;
	chapterDescription?: string;
	title: string;
	slug: string;
	key?: string;
	duration?: number;

	// Video specific fields
	transcript?: string;
	transcriptPreview?: string;
	hasTranscript?: boolean;
	estimatedWatchTime?: number;

	// PDF and Tutorial specific fields
	content?: string;
	contentPreview?: string;
	hasContent?: boolean;
	estimatedReadTime?: number;
	wordCount?: number;

	// Common fields
	searchableContent?: string;
	url?: string;
	fileExtension?: string;
	domain?: string;

	// Chunking fields
	isChunk?: boolean;
	chunkIndex?: number;
	totalChunks?: number;
	parentObjectID?: string;

	// Completion status (this might come from user data)
	isCompleted?: boolean;

	// Algolia highlighting
	_highlightResult?: {
		title?: { value: string; matchLevel: string };
		content?: { value: string; matchLevel: string };
		transcript?: { value: string; matchLevel: string };
		contentPreview?: { value: string; matchLevel: string };
		transcriptPreview?: { value: string; matchLevel: string };
		chapterTitle?: { value: string; matchLevel: string };
		chapterDescription?: { value: string; matchLevel: string };
		searchableContent?: { value: string; matchLevel: string };
	};
}

export interface AcademySearchProps {
	algoliaSearchToken?: string;
	isLoading?: boolean;
	onReset?: () => void;
	onSelect?: (hit: Hit<AcademyHit>) => void;
}

const CustomSearchBox = memo(
	({
		onFocus,
		onReset,
		isLoading,
	}: {
		onFocus: () => void;
		onReset: () => void;
		isLoading: boolean;
	}) => {
		const { query, refine, isSearchStalled } = useSearchBox();

		const handleChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				refine(e.target.value);
			},
			[refine],
		);

		const handleClear = useCallback(() => {
			refine('');
			onReset();
		}, [refine, onReset]);

		const suffix = isSearchStalled || isLoading ? <Spin size="small" /> : null;

		return (
			<div className="relative">
				<Input
					prefix={<SearchOutlined />}
					suffix={suffix}
					className="academy-searchbox"
					placeholder="Search videos, PDFs, tutorials..."
					value={query}
					onChange={handleChange}
					onFocus={onFocus}
					allowClear
					onClear={handleClear}
					disabled={isLoading}
				/>
			</div>
		);
	},
);

CustomSearchBox.displayName = 'CustomSearchBox';

const HitItem = memo(
	({
		hit,
		onSelect,
		isLoading,
	}: {
		hit: Hit<AcademyHit>;
		onSelect: (hit: Hit<AcademyHit>) => void;
		isLoading: boolean;
	}) => {
		const getIcon = () => {
			switch (hit.type) {
				case 'video':
					return <PlayCircleOutlined className="hit-icon video-icon" />;
				case 'pdf':
					return <FilePdfOutlined className="hit-icon pdf-icon" />;
				case 'tutorial':
					return <ReadOutlined className="hit-icon tutorial-icon" />;
				default:
					return <BookOutlined className="hit-icon" />;
			}
		};

		const getTypeLabel = () => {
			switch (hit.type) {
				case 'video':
					return 'Video';
				case 'pdf':
					return 'PDF';
				case 'tutorial':
					return 'Tutorial';
				default:
					return 'Content';
			}
		};

		const getEstimatedTime = () => {
			if (hit.type === 'video' && hit.estimatedWatchTime) {
				return `${hit.estimatedWatchTime}m`;
			}
			if (hit.estimatedReadTime) {
				return `${hit.estimatedReadTime}m`;
			}
			if (hit.duration) {
				const minutes = Math.floor(hit.duration / 60);
				return `${minutes}m`;
			}
			return null;
		};

		// Get the best available description/content preview
		const getContentPreview = () => {
			if (hit.type === 'video' && hit.transcriptPreview) {
				return hit._highlightResult?.transcriptPreview?.value || hit.transcriptPreview;
			}
			if ((hit.type === 'pdf' || hit.type === 'tutorial') && hit.contentPreview) {
				return hit._highlightResult?.contentPreview?.value || hit.contentPreview;
			}
			if (hit.chapterDescription) {
				return hit._highlightResult?.chapterDescription?.value || hit.chapterDescription;
			}
			return null;
		};

		const contentPreview = getContentPreview();
		const estimatedTime = getEstimatedTime();

		return (
			<div
				onClick={() => !isLoading && onSelect(hit)}
				className={`academy-hit-item ${isLoading ? 'pointer-events-none' : ''}`}
			>
				<div className="hit-content">
					<div className="hit-header">
						<div className="hit-icon-container">{getIcon()}</div>
						<div className="hit-main">
							<div className="hit-title">
								<span
									dangerouslySetInnerHTML={{
										__html: hit._highlightResult?.title?.value || hit.title,
									}}
								/>
							</div>
							<div className="hit-meta">
								<span className="content-type">{getTypeLabel()}</span>
								<span className="chapter-info">
									<span
										dangerouslySetInnerHTML={{
											__html: hit._highlightResult?.chapterTitle?.value || hit.chapterTitle,
										}}
									/>
								</span>
								{estimatedTime && <span className="duration">{estimatedTime}</span>}
							</div>
						</div>
					</div>

					{contentPreview && (
						<div className="hit-description">
							<span
								dangerouslySetInnerHTML={{
									__html: contentPreview,
								}}
							/>
						</div>
					)}
				</div>
			</div>
		);
	},
);

HitItem.displayName = 'HitItem';

const CustomHits = memo(
	({
		onSelect,
		isVisible,
		isLoading,
	}: {
		onSelect: (hit: Hit<AcademyHit>) => void;
		isVisible: boolean;
		isLoading: boolean;
	}) => {
		const { hits } = useHits<Hit<AcademyHit>>();
		const { query } = useSearchBox();

		if (!isVisible) {
			return null;
		}

		if (!query.trim()) {
			return (
				<div className="academy-hits-container">
					<div className="search-prompt">
						<SearchOutlined className="search-prompt-icon" />
						<p>Start typing to search through academy content...</p>
					</div>
				</div>
			);
		}

		if (!hits?.length) {
			return (
				<div className="academy-hits-container">
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="No results found"
						className="no-results"
					/>
				</div>
			);
		}

		return (
			<div className="academy-hits-container">
				<div className="hits-header">
					<span className="results-count">
						{hits.length} result{hits.length !== 1 ? 's' : ''} found
					</span>
				</div>
				<div className="hits-list">
					{hits.map((hit) => (
						<HitItem key={hit.objectID} hit={hit} onSelect={onSelect} isLoading={isLoading} />
					))}
				</div>
			</div>
		);
	},
);

CustomHits.displayName = 'CustomHits';

const AcademySearch: React.FC<AcademySearchProps> = ({ isLoading = false, onReset }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [showHits, setShowHits] = useState(false);
	const navigate = useNavigate();

	const algoliaSearchClient: any = useMemo(
		() =>
			algoliasearch(
				import.meta.env.VITE_APP_ALGOLIA_APP_ID,
				import.meta.env.VITE_APP_ALGOLIA_SEARCH_KEY,
			),
		[],
	);

	const handleSelect = useCallback(
		(hit: Hit<AcademyHit>) => {
			setShowHits(false);

			// Blur the search input
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}

			// Navigate to the appropriate content page based on type
			let route = '';
			switch (hit.type) {
				case 'video':
					route = AppRoutes.RESELLER_ACADEMY_VIDEO.replace(
						':chapterId',
						hit.chapterId.toString(),
					).replace(':videoSlug', hit.slug);
					break;
				case 'pdf':
					route = AppRoutes.RESELLER_ACADEMY_PDF.replace(
						':chapterId',
						hit.chapterId.toString(),
					).replace(':pdfSlug', hit.slug);
					break;
				case 'tutorial':
					route = AppRoutes.RESELLER_ACADEMY_TUTORIAL.replace(
						':chapterId',
						hit.chapterId.toString(),
					).replace(':tutorialSlug', hit.slug);
					break;
				default:
					// Fallback to chapter page
					route = AppRoutes.RESELLER_ACADEMY_CHAPTER.replace(
						':chapterId',
						hit.chapterId.toString(),
					);
			}

			navigate(route);
		},
		[navigate],
	);

	const handleFocus = useCallback(() => {
		if (!isLoading) {
			setShowHits(true);
		}
	}, [isLoading]);

	const handleReset = useCallback(() => {
		setShowHits(false);
		onReset?.();
	}, [onReset]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setShowHits(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const configureProps = useMemo(
		() => ({
			hitsPerPage: 20,
			attributesToHighlight: [
				'title',
				'content', // PDF and Tutorial content
				'transcript', // Video transcripts
				'chapterTitle',
				'contentPreview',
				'transcriptPreview',
				'chapterDescription',
				'searchableContent',
			],
			highlightPreTag: '<mark>',
			highlightPostTag: '</mark>',
			// Use the same searchable attributes as configured in the backend
			attributesToSnippet: [
				'content:100', // Show 100 characters of PDF/Tutorial content
				'transcript:80', // Show 80 characters of transcript
				'contentPreview:50', // Show 50 characters of preview
				'transcriptPreview:50',
			],
		}),
		[],
	);

	return (
		<div className="academy-search-wrapper" ref={containerRef}>
			<InstantSearch
				searchClient={algoliaSearchClient}
				indexName="academy_content"
				future={{
					preserveSharedStateOnUnmount: true,
				}}
			>
				<Configure {...configureProps} />
				<div className="academy-search-container">
					<CustomSearchBox onFocus={handleFocus} onReset={handleReset} isLoading={isLoading} />
					<CustomHits onSelect={handleSelect} isVisible={showHits} isLoading={isLoading} />
				</div>
			</InstantSearch>
		</div>
	);
};

export default AcademySearch;
